import { useState, useCallback } from 'react';
import { fetchTrends, fetchTrendsV2, fetchTrendsV3 } from '../services/aimlService';
import { format } from 'date-fns';

const useTrends = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const getTrends = useCallback(async (date, locationNames, trendType = 'Daily', dateRange = {}) => {
        setLoading(true);
        setError(null);
        try {
            // Ensure date is in YYYY-MM-DD format
            const dateStr = date instanceof Date ? format(date, 'yyyy-MM-dd') : date;

            console.log("Hook: calling service with", { dateStr, locationNames, trendType, dateRange });

            let response;
            if (trendType === 'Range' || trendType === 'Weekly') {
                // Endpoint v3
                // Expecting dateRange to have fromDate and toDate
                const { fromDate, toDate } = dateRange;
                if (!fromDate || !toDate) {
                    // Fallback or error? 
                    // If fromDate/toDate missing, maybe user didn't select them yet.
                    // But isFormValid in FilterPanel ensures they are set.
                    console.warn("Missing fromDate/toDate for Range trend, using single date as fallback or failing.");
                }
                response = await fetchTrendsV3(fromDate, toDate, locationNames);
            } else if (locationNames && locationNames.length > 1) {
                // Endpoint v2 (Multi-station Daily)
                response = await fetchTrendsV2(dateStr, locationNames);
            } else {
                // Endpoint v1 (Single-station Daily)
                response = await fetchTrends(dateStr, locationNames);
            }

            console.log("Hook: received response", response);

            let processedData = {};

            // Helper to check if response is multi-station (keys are station names)
            // We assume if it's not the standard structure (containing 'dailyTrends' or 'records'), it's likely the multi-station map.
            // Or explicitly check if locationNames.length > 1 and trendType != 'Range'.
            // The response log shows keys like "JAGIRVENKTAPUR", "KALMANDRAGI".

            const isMultiStation = locationNames && locationNames.length > 1;

            if (isMultiStation && !response.dailyTrends) {
                // Normalize Multi-Station Data
                const stations = Object.keys(response).filter(k => k !== 'msg' && k !== 'code');

                const timeMap = {};
                let combinedAnomalies = [];
                let clusterInfo = { clusterSizes: {}, anomalies: [], locationTopCluster: {}, numCentroids: 0 };

                stations.forEach(station => {
                    const stationResponse = response[station];
                    const stationRecords = stationResponse?.dailyTrends?.records ||
                        (Array.isArray(stationResponse) ? stationResponse : []);

                    if (stationRecords.length > 0) {
                        stationRecords.forEach(record => {
                            const t = record.time ? record.time.split('[')[0] : record.time;
                            if (!timeMap[t]) timeMap[t] = { time: t };

                            Object.keys(record).forEach(key => {
                                if (key !== 'time' && key !== 'stationName' && key !== 'id') {
                                    timeMap[t][`${station}_${key}`] = record[key];
                                }
                            });
                        });
                    }
                    if (stationResponse?.anomalies) {
                        combinedAnomalies = [...combinedAnomalies, ...stationResponse.anomalies];
                    }
                    if (stationResponse?.clusterInsights) {
                        clusterInfo = stationResponse.clusterInsights;
                    }
                });

                processedData = {
                    dailyTrends: { records: Object.values(timeMap) },
                    yearlyTrends: { records: [] },
                    clusterInsights: clusterInfo,
                    anomalies: combinedAnomalies,
                    modelVersion: response[stations[0]]?.modelVersion || 'v1.0'
                };

            } else {
                // Standard Single Station or Range Response
                // Check if response is a Map (e.g. V3 or V2 single) which lacks 'dailyTrends' at top level
                if (!response.dailyTrends && Object.keys(response).length > 0 && typeof response === 'object') {
                    // It's likely a Map<StationName, TrendResponse>. 
                    // For single station or Range view (where we show one chart), pick the first station.
                    const firstKey = Object.keys(response)[0];
                    const stationData = response[firstKey] || {};

                    processedData = {
                        dailyTrends: stationData.dailyTrends || { records: [] },
                        yearlyTrends: stationData.yearlyTrends,
                        clusterInsights: stationData.clusterInsights,
                        anomalies: stationData.anomalies,
                        modelVersion: stationData.modelVersion
                    };
                } else {
                    // Standard V1 or correctly structured response
                    processedData = {
                        dailyTrends: response.dailyTrends || (trendType === 'Range' ? { records: response } : { records: [] }),
                        yearlyTrends: response.yearlyTrends,
                        clusterInsights: response.clusterInsights,
                        anomalies: response.anomalies,
                        modelVersion: response.modelVersion
                    };
                }
            }

            // Map the response fields as requested
            setData({
                dailyTrends: processedData.dailyTrends,
                yearlyTrends: processedData.yearlyTrends,
                clusterInsights: processedData.clusterInsights,
                anomalies: processedData.anomalies,
                modelVersion: processedData.modelVersion
            });

            console.log(`Hook: Data loaded.`);

            return response;
        } catch (err) {
            console.error("Failed to fetch trends:", err);
            setError(err.message || "An unexpected error occurred");
            setData(null);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        // Individual pieces of state exposed for convenience, matched to User Request
        dailyTrends: data?.dailyTrends || { records: [] },
        yearlyTrends: data?.yearlyTrends || { records: [] },
        clusterInsights: data?.clusterInsights || { clusterSizes: {}, anomalies: [], locationTopCluster: {}, numCentroids: 0 },
        anomalies: data?.anomalies || [],
        modelVersion: data?.modelVersion || 'v1.0',

        // Meta state
        loading,
        error,

        // Action
        fetchTrends: getTrends,

        // Raw data if needed
        fullData: data
    };
};

export default useTrends;
